import React, { useState, useEffect } from "react";
import { Modal, ModalHeader } from "../../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../../globalComponents/ui/Button";
import {
  SettingsModalBody, SettingsModalFooter,
  SettingsFormGroup, SettingsFormLabel,
} from "../../Settings/SettingShell";
import EvidenceFormFields from "./EvidenceFormFields";
import { updateDocument } from "../../../_services/documentsService";

const flattenTags = (tags) => {
  if (!Array.isArray(tags)) return [];
  return tags.flat().map((t) => t?.value ?? t?.key).filter(Boolean);
};

const ViewEditEvidenceModal = ({ show, onClose, document: doc, financialYears, sources, onSaved, canEdit }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (doc) {
      setForm({
        financialYearId: doc.financialYearId ?? "",
        sourceId: doc.sourceId ?? "",
        subLocationId: doc.subLocationId ?? "",
        tags: flattenTags(doc.tags),
      });
      setEditing(false);
    }
  }, [doc]);

  if (!doc || !form) return null;
  const isQuantitative = doc.questionType === "quantitative";

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateDocument(doc.id, {
        documentType: doc.documentType,
        financialYearId: Number(form.financialYearId),
        questionType: doc.questionType,
        sourceId: isQuantitative && form.sourceId ? Number(form.sourceId) : undefined,
        subLocationId: isQuantitative && form.subLocationId ? Number(form.subLocationId) : undefined,
        questionId: doc.questionId ?? undefined,
        fileName: doc.fileName,
        fileMetadataId: doc.fileMetadataId,
        shouldParse: doc.shouldParse,
        tags: doc.tags ?? undefined,
        kpiIds: doc.kpiIds ?? undefined,
      });
      onSaved?.(updated);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onClose={onClose} maxWidth={640}>
      <ModalHeader icon="fas fa-file-alt" title={doc.fileName} subtitle={editing ? "Edit evidence details" : "Evidence details"} onClose={onClose} />
      <SettingsModalBody>
        {editing ? (
          <EvidenceFormFields
            financialYears={financialYears}
            financialYearId={form.financialYearId}
            onFinancialYearChange={(v) => setForm((f) => ({ ...f, financialYearId: v }))}
            sources={sources}
            sourceId={form.sourceId}
            onSourceChange={(v) => setForm((f) => ({ ...f, sourceId: v }))}
            subLocationId={form.subLocationId}
            onSubLocationChange={(v) => setForm((f) => ({ ...f, subLocationId: v }))}
            showLocation={isQuantitative}
          />
        ) : (
          <>
            <SettingsFormGroup>
              <SettingsFormLabel>Question Type</SettingsFormLabel>
              <div style={{ fontSize: 13, color: "#1e293b" }}>{isQuantitative ? "Quantitative" : "Qualitative"}</div>
            </SettingsFormGroup>
            <SettingsFormGroup style={{ marginTop: 12 }}>
              <SettingsFormLabel>Status</SettingsFormLabel>
              <div style={{ fontSize: 13, color: "#1e293b" }}>
                {doc.docSenseId ? (doc.isVerified ? "Verified" : "Analyzed by DocSense") : doc.shouldParse ? "Queued for AI parsing" : "Not parsed"}
              </div>
            </SettingsFormGroup>
            {(doc.documentsBatchId != null || doc.answerId != null) && (
              <SettingsFormGroup style={{ marginTop: 12 }}>
                <SettingsFormLabel>Linked To</SettingsFormLabel>
                <div style={{ fontSize: 13, color: "#1e293b" }}>
                  {doc.documentsBatchId != null
                    ? `Documents Batch #${doc.documentsBatchId}`
                    : `${doc.answerType === "QUALITATIVE" ? "Qualitative" : "Quantitative"} Answer #${doc.answerId}`}
                </div>
              </SettingsFormGroup>
            )}
            {form.tags.length > 0 && (
              <SettingsFormGroup style={{ marginTop: 12 }}>
                <SettingsFormLabel>Tags</SettingsFormLabel>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {form.tags.map((t) => (
                    <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20, background: "#e0f2fe", color: "#0369a1" }}>{t}</span>
                  ))}
                </div>
              </SettingsFormGroup>
            )}
          </>
        )}
      </SettingsModalBody>
      <SettingsModalFooter>
        {editing ? (
          <>
            <SecondaryButton onClick={() => setEditing(false)} disabled={saving}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSave} disabled={saving}>{saving ? "Saving…" : "Save Changes"}</PrimaryButton>
          </>
        ) : (
          <>
            <SecondaryButton onClick={onClose}>Close</SecondaryButton>
            {canEdit && <PrimaryButton onClick={() => setEditing(true)}>Edit</PrimaryButton>}
          </>
        )}
      </SettingsModalFooter>
    </Modal>
  );
};

export default ViewEditEvidenceModal;
